import { Auth } from 'aws-amplify';
import type { AxiosError, InternalAxiosRequestConfig } from 'axios';
import Axios from 'axios';
import config from 'config';
import datadogActions, { DATADOG_EVENT } from 'lib/datadog';
import { getApiCallDatadogContext } from 'lib/datadog/apiCallContext';
import handleAPIPermissionError from 'utils/handleAPIPermissionError';
import queryString from 'query-string';

const getAuthorizationHeader = async () => `Bearer ${(await Auth.currentSession()).getIdToken().getJwtToken()}`;

type ApiRequestWithMeta = InternalAxiosRequestConfig & {
  metadata?: {
    startTime: number;
  };
};

const apiClient = Axios.create({
  baseURL: config.ADMIN_API_URL,
  headers: { 'x-API-key': config.ADMIN_API_KEY },
  paramsSerializer: (prms) => queryString.stringify(prms, { arrayFormat: 'comma' }),
});

apiClient.interceptors.request.use(async (cfg) => {
  const nextConfig = cfg as ApiRequestWithMeta;
  nextConfig.metadata = { startTime: performance.now() };
  nextConfig.headers.set('Authorization', await getAuthorizationHeader());
  return nextConfig;
});

apiClient.interceptors.response.use(
  (response) => {
    const requestConfig = response.config as ApiRequestWithMeta;
    const durationMs = requestConfig.metadata?.startTime
      ? Math.round(performance.now() - requestConfig.metadata.startTime)
      : undefined;

    datadogActions.trackDatadogAction(DATADOG_EVENT.apiCall, {
      method: requestConfig.method?.toUpperCase(),
      url: requestConfig.url,
      status: response.status,
      durationMs,
      isError: false,
      ...getApiCallDatadogContext(
        requestConfig.method,
        requestConfig.url,
        response.data,
        requestConfig.params,
      ),
    });

    return response;
  },
  (error: AxiosError) => {
    const requestConfig = error.config as ApiRequestWithMeta | undefined;
    const durationMs = requestConfig?.metadata?.startTime
      ? Math.round(performance.now() - requestConfig.metadata.startTime)
      : undefined;

    const responseData = error.response?.data as { detail?: string; message?: string } | undefined;
    const errorMessage = responseData?.detail ?? responseData?.message ?? error.message;

    const entityContext = getApiCallDatadogContext(
      requestConfig?.method,
      requestConfig?.url,
      error.response?.data,
      requestConfig?.params,
    );

    datadogActions.trackDatadogAction(DATADOG_EVENT.apiCall, {
      method: requestConfig?.method?.toUpperCase(),
      url: requestConfig?.url,
      status: error.response?.status,
      durationMs,
      isError: true,
      ...entityContext,
    });

    datadogActions.trackDatadogError(error, {
      source: 'api_client',
      url: requestConfig?.url,
      method: requestConfig?.method?.toUpperCase(),
      status: error.response?.status,
      errorType: error.name,
      errorCode: error.code,
      errorMessage,
      ...entityContext,
    });

    handleAPIPermissionError(error);
    return error;
  },
);

export default apiClient;
